import { z } from 'zod';

/**
 * What the platform does about a violation (N20).
 *
 * Staff choose one per violation type, so this list is the whole vocabulary of
 * consequence on the platform — `EnforcementService` is the one door to every
 * one of them, and nothing outside it may take any of these steps.
 *
 * **Ordered lightest to heaviest**, which {@link outranksViolationAction} reads
 * directly. That ordering is not cosmetic: a member may violate twice before a
 * moderator sees either, and the second enforcement must not quietly *downgrade*
 * the first.
 *
 * - `warn` — a notice, and nothing else. The message is delivered, the account
 *   untouched. Still recorded, still appealable: a warning is what a repeat
 *   offence is measured against.
 * - `hide_message` — the message is withheld from its recipient and still shown
 *   to its sender (`isWithheldFromRecipient`). A message that vanished from its
 *   author's own screen would read as a bug rather than as a consequence.
 * - `suspend` — temporary, for the type's `suspensionDays`, restoring to
 *   whatever status the member held before (N9).
 * - `ban` — terminal for the account.
 */
export const VIOLATION_ACTIONS = ['warn', 'hide_message', 'suspend', 'ban'] as const;

export const ViolationActionSchema = z.enum(VIOLATION_ACTIONS);

export type ViolationAction = (typeof VIOLATION_ACTIONS)[number];

/**
 * Does this action need a duration on the violation type?
 *
 * Only `suspend` has an end. It is a function rather than a comment because the
 * type's create/update schemas refine on it — a `suspend` type saved with no
 * `suspensionDays` would be an indefinite suspension the elapse sweep can never
 * lift, which is a ban wearing the wrong word.
 */
export function needsSuspensionDays(action: ViolationAction): boolean {
  return action === 'suspend';
}

/**
 * Can an overturned appeal undo this?
 *
 * The three that hold a **state** can: unhide the message, restore the
 * suspension, reinstate the banned account. `warn` cannot, and saying so is the
 * honest answer rather than a gap — a notice that has been delivered cannot be
 * un-delivered, and pretending otherwise would have the appeal report success
 * for something that did not happen.
 *
 * An overturned `warn` is still *recorded* as overturned, which is the part that
 * matters: the violation stops counting against the member, and the next
 * reviewer reading their history sees a verdict that was withdrawn.
 *
 * **`ban` is reversible here, and that is a deliberate reading of N9.** The
 * lifecycle table has no edge out of `banned` (`canTransition('banned', …)` is
 * false) and must not grow a general one — but N20 requires an enforcement to be
 * appealable, and an appeal system whose *harshest* action is the only one that
 * cannot be undone inverts its own priority. The single edge exists as
 * `UserStatusService.reinstate`, reachable only from an overturned appeal,
 * contained (N28) and audited. See the dated amendment to N9.
 */
export function isReversibleViolationAction(action: ViolationAction): boolean {
  return action !== 'warn';
}

/**
 * Is `action` a heavier consequence than `other`?
 *
 * Read by `EnforcementService` when a member already has a live enforcement: a
 * second, lighter action never replaces a standing heavier one. Without it, a
 * member suspended for harassment who then trips a `warn` type would have the
 * warning recorded as their current state — and the ordering is in one place so
 * the comparison cannot be spelled two ways.
 */
export function outranksViolationAction(action: ViolationAction, other: ViolationAction): boolean {
  return VIOLATION_ACTIONS.indexOf(action) > VIOLATION_ACTIONS.indexOf(other);
}
